import React, { useState, useEffect } from "react";
import MessageBox from "./MessageBox";
import ConfirmDialog from "./ConfirmDialog";

const API_BASE_URL = window.API_BASE_URL;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedDevices, setExpandedDevices] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    password: "",
    role: "teacher",
    is_active: true
  });
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });

  // Sort users
  const sortUsers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...users].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle different data types
      if (key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (key === 'is_active') {
        aVal = a[key] ? 1 : 0;
        bVal = b[key] ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setUsers(sorted);
  };

  // Render sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Load users from backend
  useEffect(() => {
    loadUsers();
  }, []);


  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {

      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.full_name || !formData.username || !formData.email) {
      setError("Please fill in all required fields");
      return;
    }

    // Password required for new users only
    if (!editingUser && !formData.password) {
      setError("Password is required for new users");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      if (editingUser) {
        // Renaming the username migrates all of this user's data on the
        // backend — confirm first since it touches many tables.
        const usernameChanged = formData.username !== editingUser.username;
        if (usernameChanged && !window.confirm(
          `Change username from "${editingUser.username}" to "${formData.username}"?\n\n` +
          `This also moves all of this user's data (rubrics, scans, student lists, results) to the new username.`
        )) {
          setLoading(false);
          return;
        }

        // Update existing user. Send the password ONLY when the admin typed a
        // new one — a blank field means "keep current" (the backend skips it).
        const updateData = {
          username: formData.username,
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active
        };
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }

        const response = await fetch(`${API_BASE_URL}/auth/users/${editingUser.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to update user');
        }

        setMessage({ show: true, text: 'User updated successfully', type: 'success' });
        setEditingUser(null);
      } else {
        // Create new user
        const response = await fetch(`${API_BASE_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create user');
        }

        setMessage({ show: true, text: 'User created successfully', type: 'success' });
      }

      // Reset form
      setFormData({
        full_name: "",
        username: "",
        email: "",
        password: "",
        role: "teacher",
        is_active: true
      });
      setShowCreateForm(false);
      loadUsers();
    } catch (err) {

      setError(err.message || 'Operation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      full_name: user.full_name,
      username: user.username,
      email: user.email,
      password: "", // Leave blank - won't be changed
      role: user.role,
      is_active: user.is_active
    });
    setEditingUser(user);
    setShowCreateForm(true);
    setError('');
  };

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    confirm.onConfirm(); 
    setConfirm({ show: false, text: '', onConfirm: null }); 
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const handleDelete = async (userId) => {
    showConfirm('Are you sure you want to delete this user?', async () => {

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete user');
      }

      setMessage({ show: true, text: 'User deleted successfully', type: 'success' });
      loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setMessage({ show: true, text: err.message || 'Failed to delete user. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
    });
  };

  const toggleActive = async (userId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      console.log('Toggling user status for ID:', userId);
      console.log('API URL:', `${API_BASE_URL}/auth/users/${userId}/toggle-status`);

      const response = await fetch(`${API_BASE_URL}/auth/users/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('Toggle response:', data);

      if (!response.ok) {
        console.error('Toggle failed:', data);
        throw new Error(data.message || 'Failed to update user status');
      }

      setMessage({ show: true, text: 'User status updated successfully!', type: 'success' });
      loadUsers();
    } catch (err) {
      console.error('Toggle error:', err);
      setMessage({ show: true, text: err.message || 'Failed to update status. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      full_name: "",
      username: "",
      email: "",
      password: "",
      role: "teacher",
      is_active: true
    });
    setEditingUser(null);
    setShowCreateForm(false);
    setError('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // State for device data
  const [userDeviceData, setUserDeviceData] = useState({});
  const [loadingDevices, setLoadingDevices] = useState({});

  // Fetch devices for a user
  const fetchUserDevices = async (userId) => {
    try {
      console.log(`[Devices] Fetching devices for user ${userId}`);
      setLoadingDevices(prev => ({ ...prev, [userId]: true }));
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/devices/admin/users/${userId}/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`[Devices] Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[Devices] Received ${data.devices?.length || 0} devices`);
        setUserDeviceData(prev => ({
          ...prev,
          [userId]: data.devices || []
        }));
      } else {
        console.error(`[Devices] Failed response: ${response.status}`);
      }
    } catch (err) {
      console.error('[Devices] Failed to fetch user devices:', err);
      setUserDeviceData(prev => ({
        ...prev,
        [userId]: []
      }));
    } finally {
      setLoadingDevices(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Get devices for a user (from state)
  const getUserDevices = (userId) => {
    return userDeviceData[userId] || [];
  };

  // Toggle device expansion
  const toggleDeviceExpansion = async (userId) => {
    const isCurrentlyExpanded = expandedDevices[userId];
    const hasDeviceData = userDeviceData[userId]?.length > 0 || Object.keys(userDeviceData).includes(userId.toString());
    
    // If not currently expanded, fetch devices first if not already loaded
    if (!isCurrentlyExpanded && !hasDeviceData) {
      console.log(`[Devices] User ${userId} expanding, fetching devices...`);
      await fetchUserDevices(userId);
    }
    
    // Toggle expansion state
    setExpandedDevices(prev => ({
      ...prev,
      [userId]: !isCurrentlyExpanded
    }));
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Block/Unblock device
  const toggleDeviceBlock = async (userId, deviceId) => {
    try {
      const devices = userDeviceData[userId] || [];
      const device = devices.find(d => d.id === deviceId);
      
      if (!device) return;
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/devices/admin/devices/${deviceId}/block`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !device.is_active })
      });

      if (response.ok) {
        // Update local state
        setUserDeviceData(prev => ({
          ...prev,
          [userId]: prev[userId].map(d => 
            d.id === deviceId ? { ...d, is_active: !d.is_active } : d
          )
        }));
      }
    } catch (err) {
      console.error('Failed to toggle device block:', err);
      setMessage({ show: true, text: 'Failed to update device status', type: 'error' });
    }
  };

  // Delete device
  const deleteDevice = async (userId, deviceId) => {
    showConfirm('Are you sure you want to remove this device?', async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/devices/admin/devices/${deviceId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          // Update local state
          setUserDeviceData(prev => ({
            ...prev,
            [userId]: prev[userId].filter(d => d.id !== deviceId)
          }));
        }
      } catch (err) {
        console.error('Failed to delete device:', err);
        setMessage({ show: true, text: 'Failed to remove device', type: 'error' });
      }
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      {/* Modern Header */}
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            <i className="fa-solid fa-users-gear"></i>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)', fontWeight: '700' }}>User Management</h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Manage system users and their permissions</p>
          </div>
        </div>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              padding: "10px 20px",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
            }}
          >
            <i className="fa-solid fa-plus"></i>
            Create New User
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div
          style={{
            background: "var(--panel-bg, #ffffff)",
            border: "1px solid var(--border, #e0e0e0)",
            borderRadius: 12,
            padding: 24,
            marginBottom: 24,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)"
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid var(--border, #e0e0e0)'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: editingUser ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: 'white'
            }}>
              <i className={editingUser ? "fa-solid fa-user-pen" : "fa-solid fa-user-plus"}></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-primary)' }}>
              {editingUser ? "Edit User" : "Create New User"}
            </h3>
          </div>
          {error && (
            <div style={{
              padding: "12px 16px",
              background: "#fee",
              border: "1px solid #fcc",
              borderRadius: 8,
              color: "#c33",
              fontSize: 14,
              marginTop: 12
            }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border, #ddd)",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border, #ddd)",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
                {editingUser && formData.username !== editingUser.username && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "#b45309" }}>
                    Changing the username will also move all of this user's data
                    (rubrics, scans, lists, results) to the new username.
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border, #ddd)",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Password {editingUser ? "(leave blank to keep current)" : "*"}
                </label>
                <input
                  type="text"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={editingUser ? "Leave blank to keep current" : ""}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border, #ddd)",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border, #ddd)",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="teacher">Teacher</option>
                  <option value="examiner">Examiner</option>
                  <option value="administrator">Administrator</option>
                  <option value="management">Management</option>
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  id="activeCheckbox"
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <label htmlFor="activeCheckbox" style={{ fontWeight: 500, fontSize: 14, cursor: "pointer" }}>
                  Active
                </label>
              </div>
            </div>

            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
              <button
                type="submit"
                style={{
                  padding: "10px 24px",
                  background: editingUser ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                  boxShadow: editingUser ? '0 2px 8px rgba(59, 130, 246, 0.3)' : '0 2px 8px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className={editingUser ? "fa-solid fa-check" : "fa-solid fa-user-plus"}></i>
                {editingUser ? "Update User" : "Create User"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                style={{
                  padding: "10px 24px",
                  background: "var(--surface, #f5f5f5)",
                  color: "var(--text-primary, #333)",
                  border: "2px solid var(--border, #ddd)",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--surface-hover, #e0e0e0)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--surface, #f5f5f5)";
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div
        style={{
          background: "var(--panel-bg, #ffffff)",
          border: "1px solid var(--border, #e0e0e0)",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)"
        }}
      >
        {loading && users.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
            Loading users...
          </div>
        ) : error && users.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#c33" }}>
            {error}
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
            No users created yet. Click "Create New User" to add your first user.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderBottom: "2px solid var(--border, #dee2e6)" }}>
                <th 
                  onClick={() => sortUsers('full_name')}
                  style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-user" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Name{getSortIndicator('full_name')}
                  </div>
                </th>
                <th 
                  onClick={() => sortUsers('username')}
                  style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-at" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Username{getSortIndicator('username')}
                  </div>
                </th>
                <th 
                  onClick={() => sortUsers('email')}
                  style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', maxWidth: 150, cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-envelope" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Email{getSortIndicator('email')}
                  </div>
                </th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-key" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Password
                  </div>
                </th>
                <th 
                  onClick={() => sortUsers('role')}
                  style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-user-tag" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Role{getSortIndicator('role')}
                  </div>
                </th>
                <th 
                  onClick={() => sortUsers('is_active')}
                  style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-toggle-on" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Active{getSortIndicator('is_active')}
                  </div>
                </th>
                <th style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-mobile-screen" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Devices
                  </div>
                </th>
                <th 
                  onClick={() => sortUsers('created_at')}
                  style={{ padding: "14px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057', cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-calendar-plus" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Created{getSortIndicator('created_at')}
                  </div>
                </th>
                <th style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#495057' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-gear" style={{ fontSize: '12px', color: '#6c757d' }}></i>
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const userDevices = getUserDevices(user.id);
                const activeDevices = userDevices.filter(d => d.is_active);
                const isExpanded = expandedDevices[user.id];
                const isPasswordVisible = showPasswords[user.id];

                return (
                  <React.Fragment key={user.id}>
                    <tr
                      style={{
                        borderBottom: isExpanded ? "none" : "1px solid var(--border, #eee)",
                        opacity: user.is_active ? 1 : 0.5,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) e.currentTarget.style.background = 'var(--surface-hover, #f8f9fa)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-primary)" }}>
                        {user.full_name}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-primary)" }}>
                        {user.username}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-primary)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={user.email}>
                        {user.email}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-primary)", fontFamily: "monospace" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ 
                            padding: "4px 8px", 
                            background: user.display_password ? "#fff3e0" : "#f5f5f5", 
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            color: user.display_password ? "#f57c00" : "#999"
                          }}>
                            {user.display_password && isPasswordVisible ? user.display_password : '••••••••'}
                          </span>
                          <button
                            onClick={() => user.display_password ? togglePasswordVisibility(user.id) : setMessage({ show: true, text: 'Password not available.', type: 'info' })}
                            style={{
                              padding: "6px 10px",
                              background: "transparent",
                              border: "1px solid #ddd",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 14,
                              color: user.display_password ? "#666" : "#ccc",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              if (user.display_password) {
                                e.target.style.background = "#f5f5f5";
                                e.target.style.borderColor = "#999";
                                e.target.style.color = "#333";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "transparent";
                              e.target.style.borderColor = "#ddd";
                              e.target.style.color = user.display_password ? "#666" : "#ccc";
                            }}
                            title={user.display_password ? (isPasswordVisible ? "Hide password" : "Show password") : "Password not available"}
                          >
                            <i className={isPasswordVisible ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 14 }}>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            background: user.role === "administrator" ? "#e3f2fd" : user.role === "examiner" ? "#fff3e0" : user.role === "management" ? "#f3e5f5" : "#e8f5e9",
                            color: user.role === "administrator" ? "#1976d2" : user.role === "examiner" ? "#f57c00" : user.role === "management" ? "#7b1fa2" : "#388e3c"
                          }}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <button
                          onClick={() => toggleActive(user.id)}
                          style={{
                            padding: "6px 14px",
                            borderRadius: 20,
                            border: "none",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: 12,
                            background: user.is_active ? "#4caf50" : "#999",
                            color: "white"
                          }}
                        >
                          {user.is_active ? "ON" : "OFF"}
                        </button>
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <button
                          onClick={() => toggleDeviceExpansion(user.id)}
                          style={{
                            padding: "6px 12px",
                            background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                            color: "white",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                          }}
                        >
                          {activeDevices.length}/{userDevices.length}
                          <span style={{ fontSize: 10 }}>{isExpanded ? "▲" : "▼"}</span>
                        </button>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#666" }}>
                        {formatDate(user.created_at)}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                          <button
                            onClick={() => handleEdit(user)}
                            style={{
                              padding: "8px 12px",
                              background: "#2196F3",
                              color: "white",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#1976D2";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#2196F3";
                            }}
                            title="Edit user"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            style={{
                              padding: "8px 12px",
                              background: "#f44336",
                              color: "white",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#d32f2f";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#f44336";
                            }}
                            title="Delete user"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Device Management Expandable Row */}
                    {isExpanded && (
                      <tr style={{ borderBottom: "1px solid var(--border, #eee)" }}>
                        <td colSpan="9" style={{ padding: 0, background: "var(--surface, #f9fafb)" }}>
                          <div style={{ padding: "16px 24px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                                Device Management {user.role === "teacher" && <span style={{ color: "#666", fontSize: 12, fontWeight: 400 }}>(Max 2 devices)</span>}
                              </h4>
                              <span style={{ fontSize: 12, color: "#666" }}>
                                {activeDevices.length} active device{activeDevices.length !== 1 ? "s" : ""}
                              </span>
                            </div>

                            {loadingDevices[user.id] ? (
                              <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: 13 }}>
                                Loading devices...
                              </div>
                            ) : userDevices.length === 0 ? (
                              <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: 13 }}>
                                No devices logged in yet
                              </div>
                            ) : (
                              <div style={{ display: "grid", gap: 12 }}>
                                {userDevices.map((device) => (
                                  <div
                                    key={device.id}
                                    style={{
                                      background: "white",
                                      border: `2px solid ${!device.is_active ? "#f44336" : "#e0e0e0"}`,
                                      borderRadius: 8,
                                      padding: 12,
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      opacity: !device.is_active ? 0.6 : 1
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                                          {device.device_name}
                                        </span>
                                        {!device.is_active && (
                                          <span style={{
                                            padding: "2px 8px",
                                            background: "#f44336",
                                            color: "white",
                                            borderRadius: 4,
                                            fontSize: 10,
                                            fontWeight: 600
                                          }}>
                                            INACTIVE
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: 12, color: "#666" }}>
                                        <div>User Agent: {device.user_agent?.substring(0, 50)}...</div>
                                        <div>IP Address: {device.ip_address}</div>
                                        <div>Last Login: {formatDateTime(device.login_time)}</div>
                                      </div>
                                    </div>

                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button
                                        onClick={() => toggleDeviceBlock(user.id, device.id)}
                                        style={{
                                          padding: "6px 12px",
                                          background: device.is_active ? "#ff9800" : "#4caf50",
                                          color: "white",
                                          border: "none",
                                          borderRadius: 6,
                                          cursor: "pointer",
                                          fontSize: 12,
                                          fontWeight: 600
                                        }}
                                      >
                                        {device.is_active ? "Deactivate" : "Activate"}
                                      </button>
                                      <button
                                        onClick={() => deleteDevice(user.id, device.id)}
                                        style={{
                                          padding: "6px 12px",
                                          background: "#f44336",
                                          color: "white",
                                          border: "none",
                                          borderRadius: 6,
                                          cursor: "pointer",
                                          fontSize: 12,
                                          fontWeight: 600
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <MessageBox 
        message={message} 
        onClose={() => setMessage({ show: false, text: '', type: 'info' })} 
      />
      <ConfirmDialog 
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
    </div>
  );
}
